// The world beyond the square: three more districts of town, the Town Gate, and the first open-world area.
// Town zones are cozy (no mounts); open-world zones allow mounts and, where set, flying.
import type { ZoneMapData } from './map.js';
import { T, ZoneBuilder, rng, type HouseDef, type ZoneArt } from './zonebuild.js';

export type ZoneKind = 'town' | 'field';
/** Where a creature lives (tiles). */
export interface MobSpawn { kind: string; x: number; y: number }
export interface ZoneDef { id: string; name: string; kind: ZoneKind; mounts: boolean; flying: boolean; map: ZoneMapData; art: ZoneArt | null; wayshrine: [number, number] | null; mobs: MobSpawn[] }

const H = (w: number, h: number, roof: string, roofD: string, door: number, icon: string, name: string): HouseDef => ({ w, h, roof, roofD, door, icon, name });
const map = (id: string, name: string, b: ZoneBuilder, spawn: [number, number]) => { const f = b.finish(); return { data: { id, name, width: b.w, height: b.h, tileSize: 16, spawn, collision: f.collision } as ZoneMapData, art: f.art }; };

// ---------- Harbor: west of the square ----------
function harbor() {
  const b = new ZoneBuilder(36, 24);
  b.fill(0, 15, 35, 23, T.WATER).fill(0, 12, 35, 14, T.SAND).fill(2, 10, 35, 11, T.STONE).fill(34, 11, 35, 13, T.PATH);
  b.fill(8, 15, 9, 21, T.PIER).fill(20, 15, 21, 20, T.PIER).fill(28, 15, 29, 18, T.PIER);
  b.border([[34, 10, 35, 13], [0, 12, 35, 23]]);
  for (let y = 12; y < 24; y++) b.unblock(0, y).unblock(35, y);
  for (let x = 0; x < 36; x++) b.unblock(x, 23);
  b.block(34, 14, 35, 23);   // the sea is the edge
  b.house(3, 3, H(6, 5, '#4e83ad', '#3d6a8f', 2, 'shield', 'Harbor Office')).doorPath(3, 3, H(6, 5, '', '', 2, '', ''), 9);
  b.house(12, 2, H(7, 6, '#7a8a6a', '#5a6a4e', 3, 'bread', 'Fish Market')).doorPath(12, 2, H(7, 6, '', '', 3, '', ''), 9);
  b.house(24, 3, H(6, 5, '#b46a4a', '#8c4a3a', 2, '', 'Warehouse')).doorPath(24, 3, H(6, 5, '', '', 2, '', ''), 9);
  for (const x of [4, 11, 17, 23, 30]) b.lamp(x, 9);
  b.stall(14, 13).stall(25, 13);
  b.put('barrel', 6, 12).put('crate', 7, 12).put('crate', 10, 11).put('barrel', 31, 12).put('crate', 32, 12).put('barrel', 19, 9);
  b.put('boat', 11, 17, 0, false).put('boat', 23, 18, 1, false).put('boat', 31, 19, 0, false);
  b.bench(26, 9);
  b.put('wayshrine', 18, 7);
  b.scatter(11, 16, ['tuft', 'bush', 'mushroom'], [2, 2, 33, 8]);
  return { ...map('harbor', 'Harbor', b, [32, 12]) };
}

// ---------- Artisan Row: north of the square ----------
function artisan() {
  const b = new ZoneBuilder(34, 22);
  b.fill(16, 8, 17, 21, T.PATH).fill(3, 9, 30, 10, T.PATH).fill(12, 11, 21, 16, T.STONE);
  b.border([[16, 20, 17, 21]]);
  const smith = H(7, 5, '#5a5e6b', '#3f424c', 3, 'shield', 'Smithy'), wood = H(6, 5, '#b07a4a', '#8a5a34', 2, '', 'Carpenter'), weave = H(6, 5, '#a77daa', '#7d5a80', 2, 'flower', 'Weaver');
  b.house(3, 3, smith).doorPath(3, 3, smith, 9); b.house(14, 2, wood).doorPath(14, 2, wood, 9); b.house(24, 3, weave).doorPath(24, 3, weave, 9);
  b.put('anvil', 8, 11).put('barrel', 3, 11).put('crate', 4, 11).put('crate', 27, 11).put('pot', 30, 8, 1).put('pot', 22, 8, 0);
  b.put('well', 16, 13);
  for (const x of [7, 13, 20, 26]) b.lamp(x, 8);
  b.bench(13, 17).bench(19, 17);
  b.put('wayshrine', 20, 13);
  b.scatter(21, 26, ['tuft', 'bush', 'mushroom', 'rock'], [2, 12, 31, 19]);
  return map('artisan', 'Artisan Row', b, [16, 18]);
}

// ---------- Cottage Lane: east of the square, the housing district ----------
function cottages() {
  const b = new ZoneBuilder(40, 22);
  b.fill(0, 10, 39, 11, T.PATH);
  b.border([[0, 10, 1, 11]]);
  const roofs: [string, string][] = [['#db614f', '#b4473b'], ['#4e83ad', '#3d6a8f'], ['#d67599', '#a85a7a'], ['#8fbf5a', '#5e8a3a'], ['#e0a94e', '#b07a2e']];
  for (let i = 0; i < 5; i++) {
    const x = 4 + i * 7, top = H(5, 4, roofs[i][0], roofs[i][1], 2, i % 2 ? 'flower' : '', 'Cottage'), bot = H(5, 4, roofs[(i + 2) % 5][0], roofs[(i + 2) % 5][1], 2, '', 'Cottage');
    b.house(x, 4, top).doorPath(x, 4, top, 9);
    b.house(x, 14, bot);   // the far row faces away from the lane: neighbours' homes, for now
    b.put('pot', x - 1, 8, i % 3).put('pot', x + 5, 8, (i + 1) % 3);
  }
  b.put('lamp', 8, 12).put('lamp', 22, 12).put('lamp', 36, 12).put('lamp', 15, 9).put('lamp', 29, 9);
  b.put('wayshrine', 3, 13);
  b.scatter(31, 30, ['tuft', 'bush', 'mushroom'], [2, 2, 37, 19]);
  return map('cottages', 'Cottage Lane', b, [2, 11]);
}
/** Every cottage door in Cottage Lane leads home: to your own cottage. */
export const COTTAGE_DOORS: [number, number][] = [];
for (let i = 0; i < 5; i++) COTTAGE_DOORS.push([4 + i * 7 + 2, 7]);

// ---------- the Town Gate: south of the square ----------
function gate() {
  const b = new ZoneBuilder(30, 20);
  b.fill(14, 0, 15, 19, T.PATH).fill(10, 8, 19, 11, T.STONE);
  b.border([[14, 0, 15, 1], [14, 18, 15, 19]]);
  for (let x = 2; x < 28; x++) if (x < 12 || x > 17) b.put('fence_h', x, 9);   // the town wall, with the gate open in the middle
  b.put('arch', 14, 9, 0, false);
  b.lamp(12, 8).lamp(17, 8).lamp(12, 12).lamp(17, 12);
  b.put('crate', 6, 6).put('barrel', 7, 6).put('barrel', 22, 13).put('sign', 17, 15);
  b.put('wayshrine', 9, 5);
  b.scatter(41, 34, ['tuft', 'bush', 'mushroom', 'rock'], [2, 2, 27, 17]);
  return map('gate', 'Town Gate', b, [14, 2]);
}

// ---------- the Meadowlands: the first open-world area ----------
function meadow() {
  const b = new ZoneBuilder(64, 44);
  // a river from the northeast to the southwest, with a bridge on the road
  for (let y = 0; y < 44; y++) { const cx = 44 - y * 0.55 + Math.sin(y * 0.35) * 2.5; b.fill(Math.round(cx - 1.5), y, Math.round(cx + 1.5), y, T.WATER); }
  b.fill(30, 0, 31, 30, T.PATH).fill(8, 29, 56, 30, T.PATH);
  for (let y = 0; y < 44; y++) for (let x = 0; x < 64; x++) if (b.at(x, y) === T.WATER && (x === 30 || x === 31) && y < 31) b.fill(x, y, x, y, T.PIER);
  for (let y = 29; y <= 30; y++) for (let x = 0; x < 64; x++) if (b.at(x, y) === T.WATER) b.fill(x, y, x, y, T.PIER);
  b.ellipse(12, 12, 6, 4, T.WATER).ellipse(12, 12, 7.5, 5.5, T.SAND); b.ellipse(12, 12, 6, 4, T.WATER);
  b.fill(46, 34, 56, 40, T.SOIL);
  b.border([[30, 0, 31, 1]]);
  // woods in the west and south, scattered trees elsewhere
  const r = (s: number) => { let x = s; return () => { x = (x * 1103515245 + 12345) & 0x7fffffff; return x / 0x7fffffff; }; };
  const rr = r(7);
  for (let i = 0; i < 260; i++) {
    const x = 2 + Math.floor(rr() * 60), y = 2 + Math.floor(rr() * 40);
    const woods = (x < 20 && y > 20) || (y > 36 && x < 40) || (x > 52 && y < 18);
    if ((woods ? rr() < 0.75 : rr() < 0.12) && b.at(x, y) === T.GRASS && !b.blocked(x, y) && Math.abs(x - 30.5) > 2 && Math.abs(y - 29.5) > 2) b.tree(x, y);
  }
  b.house(44, 31, H(6, 3, '#b07a4a', '#8a5a34', 2, '', 'Farmhouse'));
  b.fenceH(45, 57, 33, [50]).fenceH(45, 57, 41);
  b.put('reeds', 7, 9, 0, false).put('reeds', 17, 14, 0, false).put('reeds', 9, 16, 0, false);
  b.put('sign', 33, 3).put('wayshrine', 33, 6);
  b.scatter(51, 140, ['tuft', 'tuft', 'bush', 'mushroom', 'rock'], [2, 2, 61, 41]);
  // creatures: puffballs by the water, bunnies in the open grass, beetles at the edge of the woods.
  // None live near the road in from town, so arriving is always calm.
  const mobs: MobSpawn[] = [], r2 = rng(77), want: Record<string, number> = { puff: 14, bunny: 12, beetle: 10 };
  for (let tries = 0; tries < 4000 && Object.values(want).some(n => n > 0); tries++) {
    const x = 3 + Math.floor(r2() * 58), y = 3 + Math.floor(r2() * 38);
    if (!b.free(x, y) || b.at(x, y) !== T.GRASS || (y < 12 && Math.abs(x - 30.5) < 9) || mobs.some(m => Math.abs(m.x - x) + Math.abs(m.y - y) < 4)) continue;
    let water = false; for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) if (b.at(x + dx, y + dy) === T.WATER) water = true;
    const woods = (x < 22 && y > 18) || (y > 34 && x < 42) || (x > 50 && y < 20);
    const kind = water && want.puff > 0 ? 'puff' : woods && want.beetle > 0 ? 'beetle' : !woods && want.bunny > 0 ? 'bunny' : want.puff > 0 && !woods ? 'puff' : '';
    if (!kind) continue;
    want[kind]--; mobs.push({ kind, x, y });
  }
  const out = map('meadow', 'Meadowlands', b, [30, 3]);
  out.data.mounts = true; out.data.flying = true;
  return { ...out, mobs };
}

const h = harbor(), a = artisan(), c = cottages(), g = gate(), m = meadow();
export const ZONES: ZoneDef[] = [
  { id: 'town', name: 'Tiny Town Square', kind: 'town', mounts: false, flying: false, map: null as unknown as ZoneMapData, art: null, wayshrine: [17, 19], mobs: [] },
  { id: 'harbor', name: 'Harbor', kind: 'town', mounts: false, flying: false, map: h.data, art: h.art, wayshrine: [18, 7], mobs: [] },
  { id: 'artisan', name: 'Artisan Row', kind: 'town', mounts: false, flying: false, map: a.data, art: a.art, wayshrine: [20, 13], mobs: [] },
  { id: 'cottages', name: 'Cottage Lane', kind: 'town', mounts: false, flying: false, map: c.data, art: c.art, wayshrine: [3, 13], mobs: [] },
  { id: 'gate', name: 'Town Gate', kind: 'town', mounts: false, flying: false, map: g.data, art: g.art, wayshrine: [9, 5], mobs: [] },
  { id: 'meadow', name: 'Meadowlands', kind: 'field', mounts: true, flying: true, map: m.data, art: m.art, wayshrine: [33, 6], mobs: m.mobs },
];
export const zoneDef = (id: string) => ZONES.find(z => z.id === id);
